<template>
  <div class="section">
    <div class="container is-fluid">
      <div class="level">
        <Header class="level-left" v-bind="$route.meta" />
        <b-button
          class="left-right"
          type="is-success"
          icon-left="plus"
          :disabled="selectedPlant === null"
          @click="showCreateForm()"
        >
          Manual Booking
        </b-button>
      </div>
      <b-field grouped group-multiline>
        <b-field v-if="hasGroupPlants" label="Plant">
          <b-select v-model="selectedPlant" icon="industry" expanded>
            <option v-for="plant in groupPlants" :key="plant.id" :value="plant">
              {{ plant.name }}
            </option>
          </b-select>
        </b-field>
        <b-field v-else label="Plant">
          <multiselect
            v-model="selectedPlant"
            :options="plants"
            :multiple="false"
            :close-on-select="true"
            placeholder="Pick plant"
            :custom-label="(obj) => obj.id + ' - ' + obj.name"
            track-by="name"
            style="min-width: 400px;"
          />
        </b-field>
        <b-field label="Week">
          <div class="booking-nav">
            <b-button
              icon-left="chevron-left"
              :disabled="isLoading"
              @click="shiftWeek(-1)"
            />
            <span class="booking-nav__label">{{ weekLabel }}</span>
            <b-button
              icon-left="chevron-right"
              :disabled="isLoading"
              @click="shiftWeek(1)"
            />
            <b-button :disabled="isLoading" @click="goToThisWeek()">
              This week
            </b-button>
          </div>
        </b-field>
        <b-field label="Jump to">
          <b-datepicker
            v-model="pickedDate"
            name="weekpick"
            :first-day-of-week="1"
            :locale="'en-CA'"
            icon="calendar-alt"
            placeholder="Any day of the week"
          />
        </b-field>
        <b-field label="Load">
          <b-button
            type="is-warning"
            icon-left="arrows-rotate"
            :disabled="isLoading || selectedPlant === null"
            @click="getData()"
          >
            Load
          </b-button>
        </b-field>
      </b-field>
      <div class="booking-summary">
        <span>
          <b>{{ allBookings.length }}</b> truck(s) this week
        </span>
        <span v-if="slotLimit">
          Daily limit: <b>{{ slotLimit }}</b>
        </span>
        <span v-else class="has-text-grey">
          No daily slot limit set for this site — add one in Target Stock Management
        </span>
        <span v-if="overLimitDays.length > 0" class="has-text-danger">
          <b-icon icon="triangle-exclamation" size="is-small" />
          {{ excessTotal }} truck(s) over the limit on {{ overLimitDays.length }} day(s)
        </span>
      </div>
      <div class="booking-legend">
        <span><i class="booking-swatch booking-swatch--out" /> Delivery / Transfer out</span>
        <span><i class="booking-swatch booking-swatch--in" /> Collect / Transfer in</span>
        <span><i class="booking-swatch booking-swatch--manual" /> Manual booking</span>
        <span><i class="booking-swatch booking-swatch--done" /> Scanned — locked</span>
        <span><i class="booking-swatch booking-swatch--excess" /> Over daily slots</span>
      </div>
      <b-loading v-model="isLoading" :is-full-page="false" />
      <div class="booking-week">
        <div
          v-for="day in days"
          :key="day.date"
          class="booking-day"
          :class="{
            'booking-day--today': day.isToday,
            'booking-day--over': day.isOver,
            'booking-day--drop': dragOverDate === day.date
          }"
          @dragover.prevent="dragOverDate = day.date"
          @dragleave="onDragLeave(day)"
          @drop.prevent="onDrop($event, day)"
        >
          <div class="booking-day__head">
            <span>{{ day.label }}</span>
            <span
              class="booking-day__count"
              :class="{ 'booking-day__count--over': day.isOver }"
              :title="day.isOver ? (day.excess + ' truck(s) over the daily limit') : ''"
            >
              {{ day.bookings.length }}<template v-if="slotLimit"> / {{ slotLimit }}</template>
            </span>
          </div>
          <div class="booking-day__body">
            <template v-for="(booking, index) in day.bookings">
              <div
                v-if="isFirstExcess(day, index)"
                :key="'over-' + booking.key"
                class="booking-day__overflow"
              >
                Over limit ({{ day.excess }})
              </div>
              <div
                :key="booking.key"
                class="booking-card"
                :class="{
                  'booking-card--out': booking.direction === 'out',
                  'booking-card--in': booking.direction === 'in',
                  'booking-card--manual-entry': booking.source === 'manual',
                  'booking-card--done': booking.done,
                  'booking-card--excess': isExcess(day, index),
                  'booking-card--missing': booking.missingInSap,
                  'booking-card--moved': booking.manualDate !== null
                }"
                :draggable="isMovable(booking)"
                :title="cardTitle(booking)"
                @dragstart="onDragStart($event, booking)"
                @dragend="onDragEnd()"
                @click="openCard(booking)"
              >
                <div class="booking-card__head">
                  <span>{{ booking.time || '--:--' }}</span>
                  <span class="booking-card__tag">{{ booking.requirement }}</span>
                  <span class="booking-card__truncate">{{ booking.ob }}</span>
                </div>
                <div class="booking-card__line booking-card__muted">
                  <span v-if="booking.source === 'manual'" class="booking-card__truncate">
                    Type {{ booking.materials.join(', ') || '—' }}
                  </span>
                  <span v-else class="booking-card__truncate">RO {{ booking.ro || '—' }}</span>
                  <span class="booking-card__truncate">{{ booking.crates }} cr.</span>
                </div>
                <div class="booking-card__truncate">
                  {{ booking.origin || '—' }} <b>&rarr;</b> {{ booking.destination || '—' }}
                </div>
                <div class="booking-card__truncate booking-card__muted">
                  {{ booking.forwarder || '—' }}
                </div>
                <div v-if="booking.source !== 'manual'" class="booking-card__truncate booking-card__muted">
                  VA {{ booking.vakey || '—' }}
                </div>
                <div class="booking-card__line booking-card__footer">
                  <span v-if="booking.source === 'manual'">
                    Manual entry
                  </span>
                  <span v-else :class="{ 'booking-card__struck': booking.manualDate || booking.scanDate }">
                    SAP {{ shortDate(booking.originalDate) }}
                  </span>
                  <span
                    v-if="booking.manualDate"
                    class="has-text-weight-bold"
                    :class="{ 'booking-card__struck': booking.scanDate && booking.scanDate !== booking.manualDate }"
                  >
                    <b-icon icon="pen" size="is-small" /> {{ shortDate(booking.manualDate) }}
                  </span>
                  <span v-if="booking.scanDate" class="has-text-weight-bold" title="Actually scanned on this day">
                    <b-icon icon="check" size="is-small" /> {{ shortDate(booking.scanDate) }}
                  </span>
                  <span v-else-if="booking.done" title="Marked done in SAP with no scan date">
                    <b-icon icon="check" size="is-small" />
                  </span>
                  <span v-if="booking.missingInSap" title="Scheduled here but no longer in SAP">
                    <b-icon icon="triangle-exclamation" size="is-small" />
                  </span>
                </div>
              </div>
            </template>
            <p v-if="day.bookings.length === 0" class="booking-day__empty">
              No truck
            </p>
          </div>
        </div>
      </div>
    </div>

    <b-modal :active.sync="isCreatingBooking" scroll="keep" has-modal-card>
      <BookingForm :plant="selectedPlant" @submit="submitBooking" />
    </b-modal>

    <b-modal :active.sync="isSchedulingBooking" scroll="keep" has-modal-card>
      <form v-if="editableSchedule" @submit.prevent="submitSchedule">
        <div class="modal-card booking-modal">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Schedule Booking
              <span v-if="selectedPlant" style="font-size:1rem;">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <table class="table is-narrow is-fullwidth booking-detail">
              <tbody>
                <tr>
                  <th>VA key</th>
                  <td>{{ editableSchedule.booking.vakey || '—' }}</td>
                  <th>Shipment (RO)</th>
                  <td>{{ editableSchedule.booking.ro || '—' }}</td>
                </tr>
                <tr>
                  <th>Delivery / Collect (OB)</th>
                  <td>{{ editableSchedule.booking.ob }}</td>
                  <th>Requirement</th>
                  <td>{{ editableSchedule.booking.requirement }}</td>
                </tr>
                <tr>
                  <th>Origin</th>
                  <td>{{ editableSchedule.booking.origin || '—' }}</td>
                  <th>Destination</th>
                  <td>{{ editableSchedule.booking.destination || '—' }}</td>
                </tr>
                <tr>
                  <th>Forwarder</th>
                  <td>{{ editableSchedule.booking.forwarder || '—' }}</td>
                  <th>Crates</th>
                  <td>{{ editableSchedule.booking.crates }}</td>
                </tr>
                <tr>
                  <th>Original date (SAP)</th>
                  <td>{{ editableSchedule.booking.originalDate || '—' }}</td>
                  <th>Status</th>
                  <td>{{ editableSchedule.booking.done ? 'Scanned' : 'To do' }}</td>
                </tr>
                <tr v-if="editableSchedule.booking.scanDate">
                  <th>Scanned on</th>
                  <td colspan="3">
                    {{ editableSchedule.booking.scanDate }}
                    {{ editableSchedule.booking.scanTime || '' }}
                  </td>
                </tr>
              </tbody>
            </table>
            <b-message v-if="editableSchedule.booking.scanDate" type="is-info" size="is-small">
              This truck was scanned on {{ editableSchedule.booking.scanDate }}, so it is shown on that day. Its slot cannot be changed any more.
            </b-message>
            <b-message v-if="editableSchedule.booking.missingInSap" type="is-warning" size="is-small">
              This booking is scheduled here but SAP no longer returns it. The details above are the ones saved when it was scheduled.
            </b-message>
            <b-field grouped>
              <b-field label="Manual Date">
                <b-datepicker
                  v-model="editableSchedule.date"
                  name="manualdate"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Manual date"
                  :disabled="editableSchedule.booking.done"
                />
              </b-field>
              <b-field label="Manual Time">
                <b-input
                  v-model="editableSchedule.time"
                  type="time"
                  :disabled="editableSchedule.booking.done"
                />
              </b-field>
            </b-field>
            <b-field label="Comment">
              <b-input v-model="editableSchedule.comment" type="text" :disabled="editableSchedule.booking.done" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info" :disabled="editableSchedule.booking.done">
              {{ $t('global.submit') }}
            </button>
            <b-button
              v-if="editableSchedule.booking.manualDate && !editableSchedule.booking.done"
              type="is-warning"
              icon-left="rotate-left"
              @click="clearSchedule(editableSchedule.booking)"
            >
              Back to SAP date
            </b-button>
          </footer>
        </div>
      </form>
    </b-modal>

    <b-modal :active.sync="isEditingBooking" scroll="keep" has-modal-card>
      <form v-if="editableBooking" @submit.prevent="submitEdit">
        <div class="modal-card booking-modal">
          <header class="modal-card-head">
            <p class="modal-card-title">
              Edit Manual Booking
              <span v-if="selectedPlant" style="font-size:1rem;">{{ selectedPlant.name }}</span>
            </p>
          </header>
          <section class="modal-card-body">
            <b-field grouped>
              <b-field label="Crate Type">
                <b-select v-model="editableBooking.type" placeholder="Crate Type">
                  <option value="1">
                    1
                  </option>
                  <option value="5.1">
                    5.1
                  </option>
                  <option value="5.2">
                    5.2
                  </option>
                  <option value="5.5">
                    5.5
                  </option>
                  <option value="GPSFood">
                    GPS Food
                  </option>
                  <option value="ZEON">
                    ZEON
                  </option>
                  <option value="RHODIA">
                    RHODIA
                  </option>
                </b-select>
              </b-field>
              <b-field label="Date">
                <b-datepicker
                  v-model="editableBooking.dateInput"
                  name="date"
                  :first-day-of-week="1"
                  :locale="'en-CA'"
                  icon="calendar-alt"
                  placeholder="Date"
                />
              </b-field>
              <b-field label="Time">
                <b-input v-model="editableBooking.time" type="time" />
              </b-field>
              <b-field label="Title">
                <b-input v-model="editableBooking.title" type="text" />
              </b-field>
            </b-field>
            <b-field grouped>
              <b-field label="Number of Crates">
                <b-input v-model="editableBooking.num_crates" type="number" placeholder="Crates" />
              </b-field>
              <b-field label="Activity">
                <b-button :type="editableBooking.leaving == true ? 'is-success' : 'is-light'" @click="changeLeaving(true)">
                  Delivery/Transfer out
                </b-button>
                <b-button :type="editableBooking.leaving == false ? 'is-success' : 'is-light'" @click="changeLeaving(false)">
                  Collect/Transfer in
                </b-button>
              </b-field>
            </b-field>
            <b-field label="Comment">
              <b-input v-model="editableBooking.comment" type="text" />
            </b-field>
          </section>
          <footer class="modal-card-foot">
            <button type="submit" class="button is-info">
              {{ $t('global.submit') }}
            </button>
            <b-button type="is-danger" icon-left="xmark" @click="deleteBooking(editableBooking)">
              Delete
            </b-button>
          </footer>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import BookingForm from './bookingForm.vue'
import CrateBookings from '../../services/v3/crateBookings'
import plantSelector from '@/mixins/plantSelector'
import { restoreConsultingPlant } from '@/helpers/plants'

// SAP requirements that mean the truck leaves the plant. The rest come in.
const OUTBOUND_REQUIREMENTS = ['DEL', 'TRFO']
// Bookings with no time land after the timed ones inside their day.
const NO_TIME_SORT_KEY = '99:99'

export default {
  name: 'CrateBooking',
  components: {
    BookingForm
  },
  mixins: [plantSelector],
  data: function () {
    return {
      selectedPlant: restoreConsultingPlant(),
      weekStart: this.$dayjs().startOf('isoWeek').format('YYYY-MM-DD'),
      pickedDate: null,
      isLoading: false,
      slotLimit: null,
      bookings: [], // SAP shipments, placed at their manual date/time when set
      manualBookings: [], // free-entry bookings, for trucks SAP does not know
      draggedKey: null,
      dragOverDate: null,
      isCreatingBooking: false,
      isEditingBooking: false,
      editableBooking: null,
      isSchedulingBooking: false,
      editableSchedule: null
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user
    }),
    weekDates () {
      return [0, 1, 2, 3, 4, 5, 6].map(offset =>
        this.$dayjs(this.weekStart).add(offset, 'day').format('YYYY-MM-DD')
      )
    },
    weekLabel () {
      const start = this.$dayjs(this.weekStart)
      const end = start.add(6, 'day')
      return 'W' + start.isoWeek() + ' · ' + start.format('DD/MM') + ' - ' + end.format('DD/MM/YYYY')
    },
    allBookings () {
      return [...this.bookings, ...this.manualBookings]
    },
    days () {
      const today = this.$dayjs().format('YYYY-MM-DD')
      const limit = this.slotLimit && this.slotLimit > 0 ? this.slotLimit : null

      return this.weekDates.map((date) => {
        const dayBookings = this.allBookings
          .filter(booking => booking.date === date)
          .sort((a, b) => {
            const timeDiff = (a.time || NO_TIME_SORT_KEY).localeCompare(b.time || NO_TIME_SORT_KEY)
            return timeDiff !== 0 ? timeDiff : String(a.ob).localeCompare(String(b.ob))
          })

        return {
          date,
          label: this.$dayjs(date).format('ddd DD/MM'),
          bookings: dayBookings,
          // Index of the first truck in excess, -1 when the site has no limit.
          overflowFrom: limit !== null ? limit : -1,
          excess: limit !== null ? Math.max(0, dayBookings.length - limit) : 0,
          isOver: limit !== null && dayBookings.length > limit,
          isToday: date === today
        }
      })
    },
    overLimitDays () {
      return this.days.filter(day => day.isOver)
    },
    excessTotal () {
      return this.overLimitDays.reduce((total, day) => total + day.excess, 0)
    }
  },
  watch: {
    pickedDate (value) {
      if (!value) return
      const start = this.$dayjs(value).startOf('isoWeek').format('YYYY-MM-DD')
      if (start === this.weekStart) return
      this.weekStart = start
      this.reload()
    }
  },
  mounted: async function () {
    await this.loadPlants()
    if (this.selectedPlant && this.selectedPlant.id) {
      await this.getData()
    }
  },
  methods: {
    resetData () {
      this.bookings = []
      this.manualBookings = []
      this.slotLimit = null
    },
    shiftWeek (weeks) {
      this.weekStart = this.$dayjs(this.weekStart).add(weeks, 'week').format('YYYY-MM-DD')
      this.reload()
    },
    goToThisWeek () {
      this.weekStart = this.$dayjs().startOf('isoWeek').format('YYYY-MM-DD')
      this.reload()
    },
    // Changing week only refetches once a plant is picked; otherwise the user
    // would get the "please select a plant" toast just for browsing weeks.
    reload () {
      if (this.selectedPlant && this.selectedPlant.id) {
        this.getData()
      }
    },
    async getData () {
      if (!this.selectedPlant || !this.selectedPlant.id) {
        this.$buefy.toast.open({ message: 'Please select a plant', duration: 3000, type: 'is-danger' })
        return
      }
      sessionStorage.setItem('consultingPlant', JSON.stringify(this.selectedPlant))
      this.isLoading = true
      const start = this.weekDates[0]
      const end = this.weekDates[6]
      try {
        const [schedule, freeEntry] = await Promise.all([
          CrateBookings.getSchedule({ siteId: this.selectedPlant.id, start, end }),
          CrateBookings.getTable({ siteId: this.selectedPlant.id, start, end })
        ])
        this.slotLimit = schedule.slotLimit != null ? parseInt(schedule.slotLimit) : null
        this.bookings = (schedule.bookings || []).map(this.normalizeSap)
        this.manualBookings = (freeEntry || []).map(this.normalizeManual)
      } catch (error) {
        this.resetData()
        this.$buefy.toast.open({ message: error.message || error, duration: 3000, type: 'is-danger' })
      }
      this.isLoading = false
    },
    normalizeSap (booking) {
      return {
        key: 'sap|' + booking.ob + '|' + booking.ro,
        source: 'sap',
        ob: booking.ob,
        ro: booking.ro,
        vakey: booking.vakey,
        requirement: booking.requirement,
        direction: OUTBOUND_REQUIREMENTS.includes(booking.requirement) ? 'out' : 'in',
        origin: booking.shipFromName || booking.shipFrom || '',
        destination: booking.shipToName || booking.shipTo || '',
        shipFrom: booking.shipFrom,
        shipFromName: booking.shipFromName,
        shipTo: booking.shipTo,
        shipToName: booking.shipToName,
        forwarder: booking.forwarder || '',
        crates: booking.numCrates || 0,
        materials: booking.materials || [],
        information: booking.information || '',
        date: booking.date,
        time: booking.time || null,
        originalDate: booking.originalDate,
        manualDate: booking.manualDate || null,
        manualTime: booking.manualTime || null,
        comment: booking.comment || '',
        scheduleId: booking.scheduleId || null,
        // The day it was really scanned. When set it is also the day the card
        // sits on, whatever was planned or booked.
        scanDate: booking.scanDate || null,
        scanTime: booking.scanTime || null,
        done: booking.status === 'T',
        missingInSap: booking.missingInSap === true,
        raw: booking
      }
    },
    normalizeManual (booking) {
      // prod_booking.date comes back as a timestamp, and slicing its ISO form
      // would shift the day for any positive UTC offset, so read it as a date.
      const date = booking.date ? this.$dayjs(booking.date).format('YYYY-MM-DD') : null
      const type = String(booking.status_crates || '').split('__')[0]
      return {
        key: 'manual|' + booking.id,
        source: 'manual',
        ob: booking.title || 'Manual',
        ro: '',
        vakey: null,
        requirement: booking.leaving ? 'DEL' : 'COL',
        direction: booking.leaving ? 'out' : 'in',
        origin: booking.leaving ? (this.selectedPlant && this.selectedPlant.id) : '',
        destination: booking.leaving ? '' : (this.selectedPlant && this.selectedPlant.id),
        forwarder: '',
        crates: parseInt(booking.num_crates) || 0,
        materials: type ? [type] : [],
        information: '',
        date,
        time: booking.time || null,
        originalDate: date,
        manualDate: null,
        manualTime: booking.time || null,
        comment: booking.comment || '',
        scheduleId: null,
        scanDate: null,
        scanTime: null,
        done: false,
        missingInSap: false,
        raw: booking
      }
    },
    isExcess (day, index) {
      return day.overflowFrom >= 0 && index >= day.overflowFrom
    },
    isFirstExcess (day, index) {
      return day.overflowFrom >= 0 && index === day.overflowFrom
    },
    // A scanned truck already came and went, so its slot is history.
    isMovable (booking) {
      return !booking.done
    },
    shortDate (date) {
      return date ? this.$dayjs(date).format('DD/MM') : '—'
    },
    cardTitle (booking) {
      const lines = [
        (booking.source === 'manual' ? 'Manual booking' : booking.requirement + ' ' + booking.ob),
        'VA key: ' + (booking.vakey || '—'),
        'Shipment (RO): ' + (booking.ro || '—'),
        'Origin: ' + (booking.origin || '—'),
        'Destination: ' + (booking.destination || '—'),
        'Forwarder: ' + (booking.forwarder || '—'),
        'Crates: ' + booking.crates,
        'Original date: ' + (booking.originalDate || '—'),
        'Manual date: ' + (booking.manualDate || '—') + (booking.manualTime ? ' ' + booking.manualTime : '')
      ]
      if (booking.scanDate) {
        lines.push('Scanned: ' + booking.scanDate + (booking.scanTime ? ' ' + booking.scanTime : '') + ' — shown on that day')
      }
      if (booking.comment) lines.push('Comment: ' + booking.comment)
      if (booking.information) lines.push('SAP info: ' + booking.information)
      if (booking.done) lines.push('Already scanned — cannot be moved')
      if (booking.missingInSap) lines.push('No longer returned by SAP')
      return lines.join('\n')
    },
    onDragStart (event, booking) {
      if (!this.isMovable(booking)) {
        event.preventDefault()
        return
      }
      this.draggedKey = booking.key
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', booking.key)
      }
    },
    onDragEnd () {
      this.draggedKey = null
      this.dragOverDate = null
    },
    onDragLeave (day) {
      if (this.dragOverDate === day.date) {
        this.dragOverDate = null
      }
    },
    onDrop (event, day) {
      const key = (event.dataTransfer && event.dataTransfer.getData('text/plain')) || this.draggedKey
      this.dragOverDate = null
      this.draggedKey = null
      const booking = this.allBookings.find(candidate => candidate.key === key)
      if (!booking || !this.isMovable(booking) || booking.date === day.date) return
      this.moveBooking(booking, day.date)
    },
    // Moving only writes to our own tables — SAP keeps its own date.
    async moveBooking (booking, date) {
      const previousDate = booking.date
      const previousManualDate = booking.manualDate
      booking.date = date
      try {
        if (booking.source === 'manual') {
          await this.persistManual(booking, { date, time: booking.time })
        } else {
          // A free-entry booking has no SAP date to differ from, so only the SAP
          // ones carry a manual date.
          booking.manualDate = date
          await this.persistSchedule(booking, { date, time: booking.manualTime, comment: booking.comment })
        }
        this.$buefy.toast.open({ message: 'Booking moved to ' + date, duration: 2000, type: 'is-success' })
      } catch (error) {
        booking.date = previousDate
        booking.manualDate = previousManualDate
        this.$buefy.toast.open({ message: error.message || error, duration: 3000, type: 'is-danger' })
      }
    },
    // The SAP fields are saved alongside the manual slot so a booking moved into
    // a week stays readable even if SAP later drops or changes the shipment.
    async persistSchedule (booking, { date, time, comment }) {
      await CrateBookings.saveSchedule({
        id_site: this.selectedPlant.id,
        ob: booking.ob,
        ro: booking.ro,
        manual_date: date,
        manual_time: time || null,
        comment: comment || null,
        vakey: booking.vakey,
        requirement: booking.requirement,
        ship_from: booking.shipFrom,
        ship_from_name: booking.shipFromName,
        ship_to: booking.shipTo,
        ship_to_name: booking.shipToName,
        forwarder: booking.forwarder,
        original_date: booking.originalDate,
        num_crates: booking.crates
      })
      booking.manualDate = date
      booking.manualTime = time || null
      booking.time = time || null
      booking.comment = comment || ''
      booking.date = date
    },
    async persistManual (booking, { date, time }) {
      await CrateBookings.updateBooking({
        id: booking.raw.id,
        id_site: booking.raw.id_site,
        num_crates: booking.raw.num_crates,
        status_crates: booking.raw.status_crates,
        leaving: booking.raw.leaving,
        title: booking.raw.title,
        comment: booking.raw.comment,
        date,
        time: time || null
      })
      booking.raw.date = date
      booking.raw.time = time || null
      booking.date = date
      booking.originalDate = date
      booking.time = time || null
    },
    openCard (booking) {
      if (booking.source === 'manual') {
        this.showEditForm(booking)
      } else {
        this.openScheduleForm(booking)
      }
    },
    openScheduleForm (booking) {
      this.editableSchedule = {
        booking,
        date: booking.manualDate ? new Date(booking.manualDate + 'T00:00:00') : (booking.date ? new Date(booking.date + 'T00:00:00') : null),
        time: booking.manualTime || null,
        comment: booking.comment || ''
      }
      this.isSchedulingBooking = true
    },
    async submitSchedule () {
      const form = this.editableSchedule
      // The scan already decided where this truck sits, so a slot change would be
      // saved and then ignored.
      if (form.booking.done) {
        this.isSchedulingBooking = false
        return
      }
      if (!form.date) {
        this.$buefy.toast.open({ message: 'Pick a manual date', duration: 3000, type: 'is-danger' })
        return
      }
      const date = this.$dayjs(form.date).format('YYYY-MM-DD')
      try {
        await this.persistSchedule(form.booking, { date, time: form.time, comment: form.comment })
        this.isSchedulingBooking = false
        this.$buefy.toast.open({ message: 'Booking scheduled', duration: 3000, type: 'is-success' })
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, duration: 3000, type: 'is-danger' })
      }
    },
    async clearSchedule (booking) {
      try {
        await CrateBookings.clearSchedule({
          id: booking.scheduleId,
          id_site: this.selectedPlant.id,
          ob: booking.ob,
          ro: booking.ro
        })
        booking.manualDate = null
        booking.manualTime = null
        booking.time = null
        booking.comment = ''
        booking.scheduleId = null
        booking.date = booking.originalDate
        this.isSchedulingBooking = false
        this.$buefy.toast.open({ message: 'Booking back on its SAP date', duration: 3000, type: 'is-warning' })
        // A booking whose SAP row is gone has nothing left to show once its
        // manual slot is dropped, so refresh rather than leave a ghost card.
        if (booking.missingInSap) await this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, duration: 3000, type: 'is-danger' })
      }
    },
    showCreateForm () {
      this.isCreatingBooking = true
    },
    async submitBooking (booking) {
      this.isCreatingBooking = false
      try {
        for (const element of booking) {
          await CrateBookings.createBooking(element)
        }
        this.$buefy.toast.open({ message: 'Booking created', duration: 3000, type: 'is-success' })
        await this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, duration: 3000, type: 'is-danger' })
      }
    },
    showEditForm (booking) {
      this.editableBooking = {
        ...booking.raw,
        type: String(booking.raw.status_crates || '').split('__')[0],
        leaving: booking.raw.leaving === true,
        dateInput: booking.date ? new Date(booking.date + 'T00:00:00') : new Date(),
        time: booking.time || null
      }
      this.isEditingBooking = true
    },
    changeLeaving (leaving) {
      this.editableBooking.leaving = leaving
    },
    async submitEdit () {
      const form = this.editableBooking
      // status_crates keeps its '<type>__<rest>' shape, only the type is editable.
      const parts = String(form.status_crates || '').split('__')
      try {
        await CrateBookings.updateBooking({
          id: form.id,
          id_site: form.id_site,
          num_crates: form.num_crates,
          status_crates: parts.length > 1 ? form.type + '__' + parts.slice(1).join('__') : form.type,
          leaving: form.leaving,
          title: form.title,
          comment: form.comment,
          date: this.$dayjs(form.dateInput).format('YYYY-MM-DD'),
          time: form.time || null
        })
        this.isEditingBooking = false
        this.$buefy.toast.open({ message: 'Booking updated', duration: 3000, type: 'is-success' })
        await this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, duration: 3000, type: 'is-danger' })
      }
    },
    async deleteBooking (booking) {
      try {
        await CrateBookings.archiveBooking(booking.id)
        this.isEditingBooking = false
        this.$buefy.toast.open({ message: 'Booking Deleted', duration: 3000, type: 'is-warning' })
        await this.getData()
      } catch (error) {
        this.$buefy.toast.open({ message: error.message || error, duration: 3000, type: 'is-danger' })
      }
    }
  }
}

</script>

<style scoped lang="scss">
.booking-nav {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.booking-nav__label {
  min-width: 13rem;
  text-align: center;
  font-weight: 600;
}

.booking-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  align-items: center;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
}

.booking-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 0.6rem;
  font-size: 0.75rem;
  color: #5a5a5a;
}

.booking-swatch {
  display: inline-block;
  width: 0.85rem;
  height: 0.85rem;
  border: 1px solid #cfcfcf;
  border-left-width: 4px;
  border-radius: 2px;
  vertical-align: middle;
  background: #fff;
}

.booking-swatch--out { border-left-color: #3e8ed0; }
.booking-swatch--in { border-left-color: #48c78e; }
.booking-swatch--manual { border-left-color: #a259d6; background: #faf5ff; }
.booking-swatch--done { border-left-color: #b5b5b5; background: #f0f0f0; }
.booking-swatch--excess { border-left-color: #f14668; outline: 2px solid #f14668; outline-offset: -2px; }

.booking-week {
  display: flex;
  gap: 0.5rem;
  align-items: stretch;
  overflow-x: auto;
}

.booking-day {
  display: flex;
  flex: 1 1 0;
  min-width: 12.5rem;
  flex-direction: column;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  background: #fafafa;
}

.booking-day--today { border-color: #3e8ed0; }
.booking-day--over { border-color: #f14668; }
.booking-day--drop { background: #eef6fd; border-color: #3e8ed0; }

.booking-day__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid #dbdbdb;
  background: #f0f0f0;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: capitalize;
}

.booking-day--today .booking-day__head { background: #e4f0fb; }

.booking-day__count { font-variant-numeric: tabular-nums; }
.booking-day__count--over { color: #f14668; }

.booking-day__body {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-height: 9rem;
  max-height: 62vh;
  padding: 0.35rem;
  overflow-y: auto;
}

.booking-day__empty {
  color: #b5b5b5;
  text-align: center;
  font-size: 0.75rem;
  padding: 0.6rem 0;
}

.booking-day__overflow {
  border-top: 2px dashed #f14668;
  padding-top: 0.2rem;
  color: #f14668;
  font-size: 0.7rem;
  font-weight: 700;
  text-align: center;
  text-transform: uppercase;
}

.booking-card {
  border: 1px solid #cfcfcf;
  border-left-width: 4px;
  border-radius: 3px;
  padding: 0.3rem 0.4rem;
  background: #fff;
  font-size: 0.72rem;
  line-height: 1.3;
  cursor: pointer;
}

.booking-card:hover { border-color: #3e8ed0; }
.booking-card--out { border-left-color: #3e8ed0; }
.booking-card--in { border-left-color: #48c78e; }
.booking-card--manual-entry { border-left-color: #a259d6; background: #faf5ff; }
.booking-card--moved { box-shadow: inset 0 0 0 1px #ffe08a; }

.booking-card--done {
  border-left-color: #b5b5b5;
  background: #f2f2f2;
  color: #7a7a7a;
  cursor: default;
}

.booking-card--excess {
  outline: 2px solid #f14668;
  outline-offset: -2px;
}

.booking-card--missing { border-style: dashed; }

.booking-card__head {
  display: flex;
  justify-content: space-between;
  gap: 0.25rem;
  font-weight: 700;
}

.booking-card__tag {
  padding: 0 0.25rem;
  border-radius: 2px;
  background: rgba(0, 0, 0, 0.06);
  font-size: 0.65rem;
}

.booking-card__line {
  display: flex;
  justify-content: space-between;
  gap: 0.25rem;
}

.booking-card__footer {
  margin-top: 0.15rem;
  border-top: 1px dotted #dbdbdb;
  padding-top: 0.1rem;
  font-size: 0.68rem;
}

.booking-card__muted { color: #7a7a7a; }
.booking-card__struck { text-decoration: line-through; }

.booking-card__truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.booking-modal {
  min-width: 46rem;
  width: fit-content;
}

.booking-detail th { width: 12%; white-space: nowrap; }
.booking-detail td { width: 38%; }
</style>
